import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { useAuth } from "@/lib/auth/AuthContext";
import { ApiError } from "@/lib/api";
import { Button, FormField, Input } from "@/components/ui";
import { AuthLayout } from "./AuthLayout";

const GENERIC_CODE_ERROR = "El codigo no es valido o ha caducado. Intentalo de nuevo.";

export function TotpChallengePage() {
  const { state, submitTotpCode, submitRecoveryCode, resetToLogin } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"totp" | "recovery">("totp");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (state.status === "unauthenticated" || state.status === "session_expired") {
      navigate("/login", { replace: true });
    }
  }, [state.status, navigate]);

  if (state.status === "authenticated") {
    return <Navigate to="/" replace />;
  }
  if (state.status !== "totp_challenge") {
    return null;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === "totp") {
        await submitTotpCode(code.trim());
      } else {
        await submitRecoveryCode(code.trim());
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError("Demasiados intentos. El inicio de sesion se ha bloqueado temporalmente.");
      } else {
        setError(GENERIC_CODE_ERROR);
      }
      setCode("");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      icon={<KeyRound className="h-6 w-6" aria-hidden="true" />}
      title={mode === "totp" ? "Verificacion en dos pasos" : "Codigo de recuperacion"}
      description={
        mode === "totp"
          ? `Introduce el codigo de 6 digitos de tu aplicacion de autenticacion para ${state.email}.`
          : "Introduce uno de tus codigos de recuperacion de un solo uso."
      }
      footer={
        <div className="space-y-2">
          <button
            type="button"
            className="font-medium text-brand-700 hover:underline"
            onClick={() => {
              setMode(mode === "totp" ? "recovery" : "totp");
              setCode("");
              setError(null);
            }}
          >
            {mode === "totp" ? "Usar un codigo de recuperacion" : "Usar codigo de autenticador"}
          </button>
          <div>
            <button
              type="button"
              className="text-ink-faint hover:text-ink hover:underline"
              onClick={resetToLogin}
            >
              Volver a introducir el correo
            </button>
          </div>
        </div>
      }
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormField
          label={mode === "totp" ? "Codigo de 6 digitos" : "Codigo de recuperacion"}
          required
        >
          {(field) => (
            <Input
              {...field}
              type={mode === "totp" ? "text" : "text"}
              inputMode={mode === "totp" ? "numeric" : "text"}
              pattern={mode === "totp" ? "[0-9]*" : undefined}
              autoComplete="one-time-code"
              maxLength={mode === "totp" ? 6 : 20}
              placeholder={mode === "totp" ? "123456" : "AAAA-BBBB-CCCC"}
              required
              // eslint-disable-next-line jsx-a11y/no-autofocus -- sole field on a dedicated code-entry step
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={submitting}
              className="text-center text-lg tracking-[0.3em]"
            />
          )}
        </FormField>

        {error && (
          <p role="alert" className="rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">
            {error}
          </p>
        )}

        <Button type="submit" className="w-full" loading={submitting}>
          Verificar
        </Button>

        <p className="text-center text-xs text-ink-faint">
          Primer acceso o sin autenticador configurado?{" "}
          <Link to="/login/enroll" className="font-medium text-brand-700 hover:underline">
            Configurar autenticador
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
